/**
 * ProfileV2.js  —  Option 1 design (gradient hero card + section cards)
 * Replaces Profile.js + UserData.js + CombinedUnitPumpComponent (PumpData.js)
 *
 * APIs:
 *  GET  /user/data                    → name, email, address, phone, org_id
 *  GET  /user/unitrate/:org_id        → current_unit_rate
 *  POST /user/unitrate/:org_id        → { newRate }
 *  GET  /user/pumplist/:org_id        → [{ label, sensors:[{motor_id,location_name,capacity_hp,isBorewell}] }]
 *  PATCH /user/update-hp              → { org_id, updates:[{motor_id,capacity_in_hp}] }
 *  Password change handled by existing UpdatePassword component
 */

import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import styled, { css, createGlobalStyle } from "styled-components";
import { GlobalState } from "../../GlobalState";
import UpdatePassword from "./UpdatePassword";

/* ─── Palette ────────────────────────────────────────────────────────────── */
const GREEN  = "#237A35";
const DARK   = "#0D3318";
const LG     = "#F0FDF4";

/* ─── Styled Components ──────────────────────────────────────────────────── */
const Screen = styled.div`
  background: #F4F6F9;
  min-height: 100vh;
  font-family: 'Inter', -apple-system, sans-serif;
  padding-bottom: 40px;
`;

/* Nav */
const Nav = styled.div`
  background: #fff; padding: 14px 18px;
  display: flex; align-items: center; gap: 12px;
  border-bottom: 1px solid #EAECEF;
  position: sticky; top: 0; z-index: 20;
`;

const BackBtn = styled.button`
  width: 36px; height: 36px; border-radius: 50%;
  border: 1.5px solid #E5E7EB; background: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 18px; cursor: pointer; color: #374151;
  &:hover { border-color: ${GREEN}; color: ${GREEN}; }
`;

const NavTitle = styled.div` font-size: 17px; font-weight: 700; color: #111; `;

/* Page max-width wrapper */
const Page = styled.div`
  max-width: 500px;
  margin: 0 auto;
  padding: 20px 18px 0;
`;

/* ── Hero card ──────────────────────────────────────────────────────────── */
const HeroCard = styled.div`
  border-radius: 20px; overflow: hidden;
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  margin-bottom: 16px;
`;

const HeroBanner = styled.div`
  background: linear-gradient(135deg, ${DARK} 0%, ${GREEN} 100%);
  padding: 28px 20px 48px;
  position: relative;
  &::after {
    content: '';
    position: absolute; bottom: -1px; left: 0; right: 0; height: 32px;
    background: #fff; border-radius: 20px 20px 0 0;
  }
`;

const Avatar = styled.div`
  width: 72px; height: 72px; border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: 3px solid rgba(255,255,255,0.4);
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; font-weight: 800; color: #fff;
  margin-bottom: 12px;
  letter-spacing: -1px;
`;

const HeroName  = styled.div` font-size: 20px; font-weight: 800; color: #fff; margin-bottom: 3px; `;
const HeroEmail = styled.div` font-size: 12px; color: rgba(255,255,255,0.65); `;

const EditFab = styled.button`
  position: absolute; top: 16px; right: 16px;
  width: 34px; height: 34px; border-radius: 10px;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.3);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 14px; color: #fff;
`;

const HeroBody = styled.div` background: #fff; padding: 0 20px 4px; `;

const InfoRow = styled.div`
  display: flex; align-items: flex-start; gap: 12px;
  padding: 13px 0; border-bottom: 1px solid #F3F4F6;
  &:last-child { border-bottom: none; }
`;

const InfoIcon = styled.div`
  width: 36px; height: 36px; border-radius: 10px;
  background: ${LG}; border: 1px solid #BBF7D0;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; font-size: 16px;
`;

const InfoLabel = styled.div`
  font-size: 10px; font-weight: 700; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase; margin-bottom: 3px;
`;

const InfoVal = styled.div`
  font-size: 13px; font-weight: 500; color: #111827; line-height: 1.45;
`;

/* ── Section card ────────────────────────────────────────────────────────── */
const SectionCard = styled.div`
  background: #fff; border-radius: 16px; overflow: hidden;
  margin-bottom: 12px; border: 1px solid #EAECEF;
`;

const SectionHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px; border-bottom: 1px solid #F3F4F6;
`;

const SectionTitle = styled.div`
  font-size: 13px; font-weight: 700; color: #111;
  display: flex; align-items: center; gap: 8px;
`;

const TitleDot = styled.div`
  width: 8px; height: 8px; border-radius: 50%; background: ${GREEN};
`;

const EditBtn = styled.button`
  padding: 4px 12px; border-radius: 8px;
  border: 1.5px solid ${({ $save }) => $save ? GREEN : "#E5E7EB"};
  background: ${({ $save }) => $save ? GREEN : "#fff"};
  color: ${({ $save }) => $save ? "#fff" : "#6B7280"};
  font-size: 11px; font-weight: 600; font-family: inherit;
  cursor: pointer; display: flex; align-items: center; gap: 4px;
  transition: all .15s;
`;

const CancelBtn = styled.button`
  padding: 4px 10px; border-radius: 8px;
  border: 1.5px solid #E5E7EB; background: #fff;
  color: #4B5563; font-size: 11px; font-weight: 600; font-family: inherit;
  cursor: pointer; margin-right: 6px;
`;

/* Unit rate */
const UnitRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 16px;
`;

const UnitLeft = styled.div` font-size: 13px; color: #4B5563; `;

const UnitValPill = styled.div`
  font-size: 20px; font-weight: 800; color: ${GREEN};
  background: ${LG}; padding: 4px 14px;
  border-radius: 20px; border: 1.5px solid #BBF7D0;
`;

const UnitInput = styled.input`
  font-size: 18px; font-weight: 700; color: ${GREEN};
  background: ${LG}; border: 1.5px solid ${GREEN};
  border-radius: 12px; padding: 4px 12px; width: 100px;
  text-align: center; font-family: inherit; outline: none;
`;

const FeedbackMsg = styled.div`
  padding: 8px 16px; font-size: 11px; font-weight: 600;
  color: ${({ $error }) => $error ? "#DC2626" : GREEN};
  background: ${({ $error }) => $error ? "#FEF2F2" : LG};
  border-top: 1px solid ${({ $error }) => $error ? "#FECACA" : "#BBF7D0"};
`;

/* Pump table */
const TableHeader = styled.div`
  display: flex; padding: 8px 16px;
  background: #F9FAFB; border-bottom: 1px solid #F3F4F6;
`;

const Th = styled.span`
  font-size: 10px; font-weight: 700; color: #4B5563;
  letter-spacing: .06em; text-transform: uppercase;
  &:first-child { flex: 1; }
`;

const PumpRow = styled.div`
  display: flex; align-items: center; padding: 11px 16px;
  border-bottom: 1px solid #F3F4F6;
  &:last-child { border-bottom: none; }
`;

const PumpName = styled.span`
  font-size: 12px; font-weight: 500; color: #374151; flex: 1;
`;

const TypeBadge = styled.span`
  font-size: 9px; font-weight: 700; text-transform: uppercase;
  padding: 2px 7px; border-radius: 6px; margin-right: 10px;
  ${({ $borewell }) => $borewell ? css`
    color: #D97706; background: #FFFBEB;
  ` : css`
    color: #1D4ED8; background: #EFF6FF;
  `}
`;

const HpBadge = styled.div`
  font-size: 12px; font-weight: 700;
  padding: 3px 10px; border-radius: 8px;
  min-width: 52px; text-align: center;
  border: 1px solid ${({ $borewell }) => $borewell ? "#FDE68A" : "#BFDBFE"};
  color: ${({ $borewell }) => $borewell ? "#D97706" : "#1D4ED8"};
  background: ${({ $borewell }) => $borewell ? "#FFFBEB" : "#EFF6FF"};
`;

const HpInput = styled.input`
  font-size: 12px; font-weight: 700;
  width: 60px; text-align: center;
  border: 1.5px solid ${GREEN}; border-radius: 8px;
  padding: 3px 6px; font-family: inherit; outline: none;
  background: ${LG}; color: ${GREEN};
`;

/* Change password button */
const PwdBtn = styled.button`
  width: 100%; padding: 14px;
  background: ${DARK}; color: #fff;
  border: none; border-radius: 14px;
  font-size: 14px; font-weight: 700; font-family: inherit;
  cursor: pointer; display: flex; align-items: center;
  justify-content: center; gap: 8px; margin-top: 4px;
  transition: background .15s;
  &:hover { background: ${GREEN}; }
`;

const LoadingBox = styled.div`
  padding: 60px; text-align: center; color: #4B5563; font-size: 14px;
`;

/* ─── Helpers ────────────────────────────────────────────────────────────── */
function initials(name = "") {
  return name.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase();
}

/* ─── Main Component ─────────────────────────────────────────────────────── */
export default function ProfileV2() {
  const history = useHistory();
  const state   = useContext(GlobalState);

  /* user data */
  const [userData,    setUserData]    = useState(null);
  const [loading,     setLoading]     = useState(true);

  /* unit rate */
  const [unitValue,    setUnitValue]    = useState("");
  const [unitOriginal, setUnitOriginal] = useState("");
  const [editingUnit,  setEditingUnit]  = useState(false);
  const [unitMsg,      setUnitMsg]      = useState({ text:"", error:false });
  const [lastModified, setLastModified] = useState("");

  /* pump hp */
  const [pumpData,        setPumpData]        = useState([]);
  const [updatedPumpData, setUpdatedPumpData]  = useState([]);
  const [editingPumps,    setEditingPumps]     = useState(false);
  const [pumpMsg,         setPumpMsg]          = useState({ text:"", error:false });

  /* change password modal */
  const [showPwd, setShowPwd] = useState(false);

  /* ── Fetch user data ── */
  useEffect(() => {
    async function fetchAll() {
      if (!state.token) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/user/data`,
          { headers: { Authorization: state.token[0] } }
        );
        setUserData(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    fetchAll();
  }, [state.token]);

  /* ── Fetch unit rate + pump list once we have org_id ── */
  useEffect(() => {
    if (!userData?.org_id || !state.token) return;
    const oid = userData.org_id;

    /* Unit rate */
    axios.get(
      `${process.env.REACT_APP_API}/user/unitrate/${oid}`,
      { headers: { Authorization: state.token[0] } }
    ).then(r => {
      const val = parseFloat(r.data?.current_unit_rate);
      const str = !isNaN(val) ? val.toFixed(2) : "7.35";
      setUnitValue(str);
      setUnitOriginal(str);
      if (r.data?.updatedAt)
        setLastModified(new Date(r.data.updatedAt).toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"}));
    }).catch(() => { setUnitValue("7.35"); setUnitOriginal("7.35"); });

    /* Pump list */
    axios.get(
      `${process.env.REACT_APP_API}/user/pumplist/${oid}`,
      { headers: { Authorization: state.token[0] } }
    ).then(r => {
      setPumpData(JSON.parse(JSON.stringify(r.data)));
      setUpdatedPumpData(JSON.parse(JSON.stringify(r.data)));
    }).catch(console.error);
  }, [userData, state.token]);

  /* ── Save unit rate ── */
  const handleUnitSave = async () => {
    setUnitMsg({ text:"", error:false });
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/user/unitrate/${userData.org_id}`,
        { newRate: parseFloat(unitValue) },
        { headers: { Authorization: state.token[0] } }
      );
      setUnitOriginal(unitValue);
      setEditingUnit(false);
      setUnitMsg({ text:"Rate updated successfully", error:false });
      setTimeout(() => setUnitMsg({ text:"", error:false }), 3000);
    } catch {
      setUnitMsg({ text:"Failed to update rate", error:true });
    }
  };

  /* ── Save HP ── */
  const handleHPSave = async () => {
    setPumpMsg({ text:"", error:false });
    try {
      const updates = [];
      updatedPumpData.forEach(section =>
        section.sensors.forEach(item =>
          updates.push({ motor_id: item.motor_id, capacity_in_hp: item.capacity_hp })
        )
      );
      await axios.patch(
        `${process.env.REACT_APP_API}/user/update-hp`,
        { org_id: userData.org_id, updates },
        { headers: { Authorization: state.token[0] } }
      );
      const refreshed = await axios.get(
        `${process.env.REACT_APP_API}/user/pumplist/${userData.org_id}`,
        { headers: { Authorization: state.token[0] } }
      );
      setPumpData(JSON.parse(JSON.stringify(refreshed.data)));
      setUpdatedPumpData(JSON.parse(JSON.stringify(refreshed.data)));
      setEditingPumps(false);
      setPumpMsg({ text:"HP values updated", error:false });
      setTimeout(() => setPumpMsg({ text:"", error:false }), 3000);
    } catch {
      setPumpMsg({ text:"Failed to update HP", error:true });
    }
  };

  if (loading) return <LoadingBox>Loading profile…</LoadingBox>;

  const name    = userData?.user_name || userData?.name || "";
  // Show the user's email; fall back to their mobile number
  const email   = userData?.user_email || userData?.email || userData?.contact || "";
  const address = userData?.address || "";

  /* All sensors flat for display */
  const allMotors = (pumpData || []).flatMap(s => s.sensors || []);

  return (
    <Screen>
      {/* NAV */}
      <Nav>
        <BackBtn onClick={() => history.push("/")}>←</BackBtn>
        <NavTitle>My Profile</NavTitle>
      </Nav>

      <Page>

        {/* ── HERO CARD ── */}
        <HeroCard>
          <HeroBanner>
            <EditFab title="Edit profile">✎</EditFab>
            <Avatar>{initials(name)}</Avatar>
            <HeroName>{name}</HeroName>
            <HeroEmail>{email}</HeroEmail>
          </HeroBanner>

          <HeroBody>
            {address && (
              <InfoRow>
                <InfoIcon>📍</InfoIcon>
                <div>
                  <InfoLabel>Address</InfoLabel>
                  <InfoVal>{address}</InfoVal>
                </div>
              </InfoRow>
            )}
          </HeroBody>
        </HeroCard>

        {/* ── UNIT RATE ── */}
        <SectionCard>
          <SectionHeader>
            <SectionTitle>
              <TitleDot />
              Electricity Unit Rate
              {lastModified && (
                <span style={{fontSize:10,fontWeight:400,color:"#4B5563"}}>
                  · updated {lastModified}
                </span>
              )}
            </SectionTitle>
            {editingUnit ? (
              <div style={{display:"flex",gap:4}}>
                <CancelBtn onClick={() => { setEditingUnit(false); setUnitValue(unitOriginal); }}>
                  Cancel
                </CancelBtn>
                <EditBtn $save onClick={handleUnitSave}>Save</EditBtn>
              </div>
            ) : (
              <EditBtn onClick={() => setEditingUnit(true)}>✎ Edit</EditBtn>
            )}
          </SectionHeader>

          <UnitRow>
            <UnitLeft>Current rate per kWh</UnitLeft>
            {editingUnit ? (
              <UnitInput
                type="number" step="0.01"
                value={unitValue}
                onChange={e => setUnitValue(e.target.value)}
                autoFocus
              />
            ) : (
              <UnitValPill>₹ {unitValue}</UnitValPill>
            )}
          </UnitRow>

          {unitMsg.text && (
            <FeedbackMsg $error={unitMsg.error}>{unitMsg.text}</FeedbackMsg>
          )}
        </SectionCard>

        {/* ── PUMPS & BOREWELLS HP ── */}
        {allMotors.length > 0 && (
          <SectionCard>
            <SectionHeader>
              <SectionTitle><TitleDot />Pumps & Borewells</SectionTitle>
              {editingPumps ? (
                <div style={{display:"flex",gap:4}}>
                  <CancelBtn onClick={() => {
                    setEditingPumps(false);
                    setUpdatedPumpData(JSON.parse(JSON.stringify(pumpData)));
                  }}>Cancel</CancelBtn>
                  <EditBtn $save onClick={handleHPSave}>Save</EditBtn>
                </div>
              ) : (
                <EditBtn onClick={() => setEditingPumps(true)}>✎ Edit HP</EditBtn>
              )}
            </SectionHeader>

            <TableHeader>
              <Th>Location</Th>
              <Th>HP</Th>
            </TableHeader>

            {(updatedPumpData || []).flatMap((section, si) =>
              (section.sensors || []).map((item, ii) => (
                <PumpRow key={`${si}-${ii}`}>
                  <PumpName>{item.location_name}</PumpName>
                  <TypeBadge $borewell={item.isBorewell}>
                    {item.isBorewell ? "Borewell" : "Pump"}
                  </TypeBadge>
                  {editingPumps ? (
                    <HpInput
                      type="number" step="0.5"
                      value={item.capacity_hp}
                      onChange={e => {
                        const nd = JSON.parse(JSON.stringify(updatedPumpData));
                        nd[si].sensors[ii].capacity_hp = e.target.value;
                        setUpdatedPumpData(nd);
                      }}
                    />
                  ) : (
                    <HpBadge $borewell={item.isBorewell}>{item.capacity_hp}</HpBadge>
                  )}
                </PumpRow>
              ))
            )}

            {pumpMsg.text && (
              <FeedbackMsg $error={pumpMsg.error}>{pumpMsg.text}</FeedbackMsg>
            )}
          </SectionCard>
        )}

        {/* ── CHANGE PASSWORD ── */}
        {showPwd ? (
          <SectionCard>
            <SectionHeader>
              <SectionTitle><TitleDot />Change Password</SectionTitle>
              <EditBtn onClick={() => setShowPwd(false)}>✕ Close</EditBtn>
            </SectionHeader>
            <div style={{padding:"0 16px 16px"}}>
              <UpdatePassword loading={loading} userData={userData} />
            </div>
          </SectionCard>
        ) : (
          <PwdBtn onClick={() => setShowPwd(true)}>
            🔒 Change Password
          </PwdBtn>
        )}

      </Page>
    </Screen>
  );
}
